<template>
    <div class="campaign-terms">
        <label v-if="value.type !== 'VISIT_BONUS'">Audience groups
            <!-- A multiple select ignores an array :value, so each option carries its own selected state. -->
            <select multiple :disabled="disabled" @change="setGroups">
                <option v-for="group in groups" :key="group.id" :value="group.id" :selected="isGroupSelected(group.id)">{{ group.name }} ({{ group.id }})</option>
            </select>
        </label>
        <p v-if="value.type !== 'VISIT_BONUS'">Preview freezes a deduplicated audience. Future group members need a new preview and issuance.</p>
        <p v-if="value.type !== 'VISIT_BONUS'">Each customer receives this campaign once, including when they belong to multiple selected groups or an issuance is retried.</p>
        <label v-if="value.type !== 'VISIT_BONUS'" class="check-label">
            <input type="checkbox" :checked="value.allow_campaign_accumulation" :disabled="disabled" @change="update('allow_campaign_accumulation', $event.target.checked)" />
            Permit accumulation with another campaign only when both campaigns allow it
        </label>
        <BenefitPolicyFields v-if="value.type === 'DIGITAL_VOUCHER'" :value="value.benefit_policy" :disabled="disabled" :new-policy="newPolicy" voucher @input="update('benefit_policy', $event)" />
        <fieldset :disabled="disabled || uploading">
            <legend>Customer-facing artwork</legend>
            <label>Primary image (JPEG, PNG, WebP or GIF)<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" @change="primaryFile = $event.target.files[0] || null" /></label>
            <label>Additional images (up to five)<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple @change="additionalFiles = Array.from($event.target.files)" /></label>
            <button type="button" :disabled="!primaryFile || additionalFiles.length > 5" @click="upload">{{ uploading ? "Uploading…" : "Upload artwork" }}</button>
            <label>Artwork description (alt text)<input :value="value.image_alt" type="text" @input="update('image_alt', $event.target.value)" /></label>
            <div v-if="value.image" class="artwork-preview">
                <img :src="assetUrl(value.image)" :alt="value.image_alt || ''" />
                <img v-for="(image, index) in value.additional_images" :key="`${image}-${index}`" :src="assetUrl(image)" :alt="`${value.image_alt || 'Campaign artwork'} ${index + 2}`" />
                <button type="button" @click="clearArtwork">Remove artwork</button>
            </div>
        </fieldset>
    </div>
</template>

<script>
import BenefitPolicyFields from "./BenefitPolicyFields.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { errMsg, getServerAssetUrl } from "../../utils/common"

export default {
    components: { BenefitPolicyFields },
    props: { value: { type: Object, required: true }, groups: { type: Array, default: () => [] }, disabled: Boolean, newPolicy: Boolean },
    data() { return { primaryFile: null, additionalFiles: [], uploading: false } },
    methods: {
        update(field, value) { this.$emit("input", { ...this.value, [field]: value }) },
        isGroupSelected(groupId) { return (this.value.group_ids || []).map(String).includes(String(groupId)) },
        setGroups(event) { this.update("group_ids", Array.from(event.target.options).filter((option) => option.selected).map((option) => option.value)) },
        assetUrl: getServerAssetUrl,
        clearArtwork() { this.$emit("input", { ...this.value, image: null, additional_images: [], image_alt: "" }) },
        async upload() {
            if (this.disabled || this.uploading || !this.primaryFile) return
            if (this.additionalFiles.length > 5) { this.$toast.error("Choose at most five additional images"); return }
            this.uploading = true
            try {
                const form = new FormData()
                form.append("image", this.primaryFile)
                this.additionalFiles.forEach((file) => form.append("additional_images", file))
                const artwork = await LoyaltyFetchApi.uploadCampaignArtwork(form)
                this.$emit("input", { ...this.value, image: artwork.image, additional_images: artwork.additional_images || [] })
                this.primaryFile = null
                this.additionalFiles = []
                this.$toast.success("Artwork uploaded. Save the campaign to attach it.")
            } catch (err) { this.$toast.error(errMsg(err, "Could not upload campaign artwork")) }
            this.uploading = false
        },
    },
}
</script>

<style scoped>
.campaign-terms { margin: 12px 0; }
label { display: grid; gap: 5px; font-size: 13px; font-weight: 600; margin: 10px 0; }
.check-label { display: flex; align-items: center; }
select, input[type=text] { width: 100%; border: 1px solid #d9dee3; border-radius: 6px; padding: 8px; background: white; }
select[multiple] { min-height: 90px; }
fieldset { padding: 12px; border: 1px solid #d5e4e1; border-radius: 8px; }
legend { font-size: 15px; font-weight: 700; }
button { padding: 8px 12px; border: 1px solid #d5e4e1; border-radius: 6px; }
.artwork-preview { display: flex; gap: 8px; align-items: flex-start; flex-wrap: wrap; }
img { max-width: 180px; max-height: 130px; object-fit: contain; }
p { color: #5f6368; font-size: 13px; }
</style>
